import { Injectable } from '@angular/core';
import { HttpClientCustom } from '../custom/http-client';
import { BehaviorSubject } from 'rxjs';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { generateUuid } from 'src/providers/tidy-session/session-uuid';
import { FilePicker } from 'src/shared/providers/file-picker/file-picker';
import { Util } from 'src/shared/util/util';
import { Aws } from 'src/providers/aws/aws';
import { DemoDataService } from 'src/shared/services/demo-data.service';

export interface AttachmentPreview {
  url: string;
  filename: string;
  isImage: boolean;
}

@Injectable()
export class Communication {

  updateMessages$ = new BehaviorSubject<boolean>(false);

  constructor(
    private httpClient: HttpClientCustom,
    private storage: TidyStorage,
    private filePicker: FilePicker,
    private util: Util,
    private aws: Aws,
    private demoDataService: DemoDataService
  ) {}

  async createProxyNumber(numberType: string) {
    const url = 'api/v1/customer/shared-inbox/create-proxy-number';
    const payload = {
      number_type: numberType,
      type: numberType
    }
    return await this.httpClient.post(url, payload);
  }

  async getProxyNumbers() {
    const url = 'api/v1/customer/shared-inbox/list-proxy-numbers?includes=chat_rooms';
    const proxyNumbers = await this.httpClient.get(url);
    const hasClientSmartNumber = proxyNumbers.some(proxyNumber => proxyNumber.number_type === 'guest');
    await this.storage.save('hasClientSmartNumber', hasClientSmartNumber);
    return proxyNumbers;
  }

  async getAllMessages(filters?: any) {
    let url = `api/v1/customer/shared-inbox/latest-messages`;
    if (filters && filters?.fromDate && filters?.untilDate) {
      url += `?filters[from_date]=${filters.fromDate}&filters[until_date]=${filters.untilDate}`;
    }
    return await this.httpClient.get(url);
  }

  async getJobMessages(jobIdsArray) {
    const url = `api/v1/customer/shared-inbox/messages?filters[job_ids]=${jobIdsArray}&includes=metadata,chat_room_key&add_chat_suggestions=true`;
    return await this.httpClient.get(url);
  }

  async getMessages(homekeeperJobId) {
    const url = `api/v1/customer/communication/${homekeeperJobId}/messages`;
    const response = await this.httpClient.get(url);
    return response.reverse();
  }

  getCallMinutes(duration) {
    const length = Math.round(duration / 60);
    const minutes = length === 1 ? ' minute' : ' minutes';
    return ` (${  length  }${minutes  })`
  }

  sendSharedInboxMessage(payload) {
    const url = 'api/v1/customer/shared-inbox/send-message';
    return this.httpClient.post(url, payload);
  }

  // addInquiries is opt-in: only pass it from a thread that renders the inquiry card.
  getProxySharedInbox(chatRoomKey, page = 1, perPage = 50, addInquiries = false): Promise<{ body: any[], totalRecords: string }> {
    let url = `api/v1/customer/shared-inbox/messages?filters[chat_room_key]=${chatRoomKey}&page=${page}&per_page=${perPage}&includes=metadata,chat_room_key&add_chat_suggestions=true`;
    if (addInquiries) {
      url += '&add_inquiries=true';
    }
    return this.httpClient.getFullRequest(url)
      .then(response => ({
          body: response.body,
          totalRecords: response.headers.get('X-Total-Records')
        }));
  }

  assignChat(chatRoomKey: string, id: number, type: string) {
    const params = {
      chat_room_key: chatRoomKey,
      assign_to: {
        id,
        type
      }
    }
    const url = 'api/v1/customer/shared-inbox/assign-chat';
    return this.httpClient.post(url, params);
  }

  getSharedInbox(proTeamId, page = 1, perPage = 50): Promise<{ body: any[], totalRecords: string }> {
    if (!proTeamId) {
      return;
    }
    const url = `api/v1/customer/shared-inbox/messages?filters[team_id]=${proTeamId}&page=${page}&per_page=${perPage}&includes=metadata,chat_room_key&add_chat_suggestions=true`;
    return this.httpClient.getFullRequest(url)
      .then(response => ({
          body: response.body,
          totalRecords: response.headers.get('X-Total-Records')
        }));
  }

  getSharedInboxWithChatRoomKey(chatRoomKey: string) {
    if (!chatRoomKey) {
      return;
    }
    const url = `api/v1/customer/shared-inbox/messages?filters[chat_room_key]=${chatRoomKey}&includes=metadata,chat_room_key&add_chat_suggestions=true`;
    return this.httpClient.get(url);
  }

  generateAISuggestions(payload) {
    const url = 'api/v1/customer/ai/trigger-feature';
    return this.httpClient.post(url, payload);
  }

  performAISuggestion(payload, chatSuggestionId) {
    const url = `api/v1/customer/chat-suggestions/${chatSuggestionId}/perform`;
    return this.httpClient.post(url, payload);
  }

  dismissAISuggestion(chatSuggestionId) {
    const payload = {
      reason: 'wrong_suggestion'
    };
    const url = `api/v1/customer/chat-suggestions/${chatSuggestionId}/hide`;
    return this.httpClient.post(url, payload);
  }

  sendSharedMessage(payload) {
    const url = 'api/v1/customer/shared-inbox/send-message';
    return this.httpClient.post(url, payload);
  }

  markMessagesAsRead(messageKeys) {
    // Marking a thread read is a side effect of simply viewing Messages, not a user action:
    // it fires on page load (desktop auto-selects the first thread) and on the refresh poll.
    // In a demo/preview session that write is blocked, which would pop the "create an account"
    // prompt before the visitor has done anything — so skip the call. Callers only use this to
    // clear their local unread state, which still happens.
    if (this.demoDataService.shouldServeTemplates()) {
      return Promise.resolve(null);
    }
    const payload = {
      chat_room_keys: messageKeys
    }
    const url = 'api/v1/customer/shared-inbox/mark-chat-as-read';
    return this.httpClient.post(url, payload);
  }

  setUpdateMessages() {
    this.updateMessages$.next(true);
  }

  getUpdateMessages() {
    return this.updateMessages$.asObservable();
  }

  async addAttachment(): Promise<AttachmentPreview> {
    const uuid = generateUuid();
    const file = await this.filePicker.pickFile();
    if (!file?.format) {
      const dataSource = file?.dataUrl;
      file.format = dataSource.split(',')[0].split('/')[1];
    }
    const isImage = file.format?.startsWith('image');
    const rawName = file.filename || `${new Date().getTime()}`;
    const safeFilename = this.normalizeFilename(rawName);
    const fileKey = `message-thread-file/${uuid}/${safeFilename}`;
    const loading = await this.util.showLoading();
    try {
      const response = await this.aws.uploadFileToS3(file.dataUrl, fileKey, file.format);
      return {
        url: response?.Location,
        filename: file.filename || 'File',
        isImage
      };
    } finally {
      loading.dismiss();
    }
  }

  private normalizeFilename(filename: string): string {
    const dotIndex = filename.lastIndexOf('.');
    const name = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
    const ext = dotIndex > 0 ? filename.substring(dotIndex) : '';
    const normalized = name
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9._-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return (normalized || `${new Date().getTime()}`) + ext.toLowerCase();
  }

}
